import { apiClient } from './api';

/**
 * Enhanced service for stakeholder management with new fields and structure
 */

/**
 * Get all stakeholders for a project
 * @param {number} projectId - Project ID
 * @returns {Promise} - The API response
 */
export const getProjectStakeholders = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/stakeholders`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project stakeholders:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      summary: '',
      stakeholders: []
    };
  }
};

/**
 * Create or update a stakeholder
 * @param {number} projectId - Project ID
 * @param {Object} stakeholderData - Stakeholder data
 * @returns {Promise} - The API response
 */
export const saveStakeholder = async (projectId, stakeholderData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/stakeholders`, stakeholderData);
    return response.data;
  } catch (error) {
    console.error('Error saving stakeholder:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Update stakeholder summary for a project
 * @param {number} projectId - Project ID
 * @param {string} summary - Summary text
 * @returns {Promise} - The API response
 */
export const updateStakeholderSummary = async (projectId, summary) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/stakeholders/summary`, { summary });
    return response.data;
  } catch (error) {
    console.error('Error updating stakeholder summary:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Remove stakeholder from project
 * @param {number} projectId - Project ID
 * @param {string} stakeholderId - Stakeholder ID
 * @returns {Promise} - The API response
 */
export const removeStakeholderFromProject = async (projectId, stakeholderId) => {
  try {
    const response = await apiClient.delete(`/projects/${projectId}/stakeholders/${stakeholderId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing stakeholder from project:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Get all stakeholders across all projects (for search/admin purposes)
 * @param {string} search - Optional search term
 * @returns {Promise} - The API response
 */
export const getAllStakeholders = async (search = '') => {
  try {
    const response = await apiClient.get(`/stakeholders${search ? `?search=${encodeURIComponent(search)}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching all stakeholders:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      stakeholders: []
    };
  }
};

/**
 * Assign existing stakeholder to project
 * @param {number} projectId - Project ID
 * @param {string} stakeholderId - Stakeholder ID
 * @returns {Promise} - The API response
 */
export const assignStakeholderToProject = async (projectId, stakeholderId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/stakeholders/${stakeholderId}/assign`);
    return response.data;
  } catch (error) {
    console.error('Error assigning stakeholder to project:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

export const downloadStakeholderTemplate = async (projectId, projectName) => {
  const response = await apiClient.get(`/projects/${projectId}/stakeholders/template`, {
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Stakeholder_Import_Template.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadStakeholderTemplateGeneric = async () => {
  const response = await apiClient.get('/stakeholders/template', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Stakeholder_Import_Template.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const previewStakeholderImport = async (projectId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/projects/${projectId}/stakeholders/import/preview`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const confirmStakeholderImport = async (projectId, stakeholders) => {
  try {
    const response = await apiClient.post(
      `/projects/${projectId}/stakeholders/import/confirm`,
      { stakeholders }
    );
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Default values for new stakeholder
 */
export const getDefaultStakeholderData = () => ({
  stakeholder_id: '', // Will be auto-generated
  name: '',
  preferred_name: '',
  role: 'Business Owner',
  organization: '',
  impact: 'Medium',
  influence: 'Medium',
  sentiment: 'Neutral',
  interests: '',
  concerns: '',
  engagement: '',
  start_date: '',
  end_date: '',
  saf_submitted: 'N/A',
  phone_number: '',
  main_email: '',
  ps_email: 'N/A',
  vpn: 'N/A',
  teams_folder_main_team: 'N/A',
  kip: 'N/A',
  jira: 'N/A',
  notes: '',
  domain_module: ''
});

/**
 * Dropdown options for various fields
 */
export const stakeholderFieldOptions = {
  roles: [
    'ABAP',
    'ABAP/BTP',
    'Accounting Manager',
    'Accounts Payable Department Mana',
    'Accounts Payable Supervisor',
    'Application Support Administrator',
    'Assistant Controller',
    'BA/ PM',
    'BTP CPI Consultant',
    'BTP Extension',
    'BTP Extension Lead',
    'Business Owner',
    'CFO',
    'CFO Ops',
    'Chef Admin Officer',
    'Chef Technology officer',
    'CMR Resource',
    'Consultant',
    'Controller, Property Operations',
    'Customer Project Manager',
    'Customer Project Sponsor',
    'Data Cleansing (JDE)',
    'Data Migration Consultant',
    'Director',
    'Director Accounting',
    'Director Corporate Service',
    'Director of Finance',
    'Director Sox',
    'Director, IT Support Services',
    'Director, Operations Reporting and Budget, Accounting And Finance',
    'End User',
    'Executive Sponsor',
    'Facilities Support Director',
    'Finance Consultant',
    'Fixed Asset Consultant',
    'Fixed Asset Manager',
    'Functional',
    'IT Portfolio Manager',
    'IT Team',
    'Manager, Software Development',
    'OCM',
    'PM',
    'PMO',
    'Procurement Specialist',
    'Project Architect',
    'Project Management Support',
    'Project Manager',
    'Property Tax Analyst',
    'QA Coordinator',
    'Real Estate Contracts Manager',
    'Retail Operations Manager',
    'SAC',
    'SAC Consultant',
    'SAP ALM',
    'SAP Basis Consultant',
    'SAP BTP CPI',
    'SAP BTP CPI Consultant',
    'SAP Cutover Lead',
    'SAP Data Consultant',
    'SAP Enable Now Expert',
    'SAP FICO',
    'SAP Finance',
    'SAP HR',
    'SAP PTP QA Lead',
    'SAP QA Analyst',
    'SAP QA PTP Lead',
    'SAP QA RTR Lead',
    'SAP Reporting',
    'SAP Reporting QA Lead',
    'SAP RTR Lead',
    'SAP Security',
    'SAP Test Analyst',
    'SAP Test Lead',
    'SELECCION Delivery Executive',
    'SELECCION Project Manager',
    'Senior Cash Control Accountant',
    'Senior Director, Finance',
    'Senior Income Tax Manager',
    'Senior Sales Tax Analyst',
    'Senior Systems Accounting Analyst',
    'Sr Manager, Property Tax Appeals',
    'Subject Matter Expert',
    'SVP Controller',
    'Technical Consultant - ABAP S/4 HANA',
    'Technical Project Support',
    'Technical SAP Consultant',
    'Training',
    'Training Consultant',
    'Vice President 3rd party ops',
    'Vice President Info Sys',
    'Vice President Internal Audit',
    'Vice President Property Tax',
    'Vice President Tax Director',
    'Vice President, Commercial Properties',
    'Vice President of Finance',
    'Vice President of Human Resources',
    'VP Sales'
  ],
  impact: ['High', 'Medium', 'Low'],
  influence: ['High', 'Medium', 'Low'],
  sentiment: ['Positive', 'Neutral', 'Negative'],
  yesNoNA: ['Yes', 'No', 'N/A'],
  safSubmitted: ['Yes', 'No', 'N/A'],
  psEmail: ['Yes', 'No', 'N/A'],
  vpn: ['Yes', 'No', 'N/A'],
  teamsFolder: ['Yes', 'No', 'N/A'],
  kip: ['Yes', 'No', 'Deactivated', 'N/A', 'Yes but not using a License'],
  jira: ['Yes', 'No', 'N/A'],
  domainModule: [
    'Finance',
    'Finance/Treasury',
    'Manufacturing',
    'R&D/Engineering',
    'Warehousing',
    'Sales',
    'Sourcing & Procurement',
    'Supply Chain',
    'Additional Content/Other'
  ]
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid email format
 */
export const isValidEmail = (email) => {
  if (!email) return true; // Empty email is valid
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format (basic validation)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid phone format
 */
export const isValidPhone = (phone) => {
  if (!phone) return true; // Empty phone is valid
  // Basic phone validation - allows various formats
  const phoneRegex = /^[\d\s\-+().]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
};

/**
 * Get custom roles from backend
 * @returns {Promise} - Array of custom roles
 */
export const getCustomRoles = async () => {
  try {
    const response = await apiClient.get('/stakeholder-roles');
    if (response.data.success) {
      return response.data.roles;
    }
    return [];
  } catch (error) {
    console.error('Error fetching custom roles:', error);
    return [];
  }
};

/**
 * Add a new custom role
 * @param {string} roleName - New role name
 * @returns {Promise} - API response
 */
export const addCustomRole = async (roleName) => {
  try {
    const response = await apiClient.post('/stakeholder-roles', { role_name: roleName });
    return response.data;
  } catch (error) {
    console.error('Error adding custom role:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Get all roles (predefined + custom)
 * @returns {Promise} - Combined array of all roles
 */
export const getAllRoles = async () => {
  try {
    const customRoles = await getCustomRoles();
    const allRoles = [...stakeholderFieldOptions.roles];
    
    // Add custom roles that aren't already in the predefined list
    customRoles.forEach(customRole => {
      if (!allRoles.includes(customRole.role_name)) {
        allRoles.push(customRole.role_name);
      }
    });
    
    // Sort alphabetically for better UX
    return allRoles.sort();
  } catch (error) {
    console.error('Error getting all roles:', error);
    return stakeholderFieldOptions.roles;
  }
};

/**
 * Validate required fields for stakeholder
 * @param {Object} stakeholder - Stakeholder data
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
export const validateStakeholder = (stakeholder) => {
  const errors = [];
  
  if (!stakeholder.name?.trim()) {
    errors.push('Name is required');
  }
  
  if (stakeholder.main_email && !isValidEmail(stakeholder.main_email)) {
    errors.push('Main email must be a valid email address');
  }
  
  if (stakeholder.phone_number && !isValidPhone(stakeholder.phone_number)) {
    errors.push('Phone number must be a valid phone number');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};